import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const DLDOfficeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [officeTypes, setOfficeTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    OfficeID: "",
    OfficeType: "",
    OfficeName: "",
    RegionalOffice: "",
    OfficeName2: "",
    AddressNo: "",
    AddressMoo: "",
    AddressRoad: "",
    AddressSubdistrict: "",
    AddressDistrict: "",
    Province: "",
    PostalCode: "",
    Email: "",
    Phone: "",
    Website: "",
    Latitude: "",
    Longitude: "",
    Remark: "",
    Status: "1",
    CMOrderNo: "",
    CMName01: "",
    CMPosition01: "",
    CMName02: "",
    CMPosition02: "",
    CMName03: "",
    CMPosition03: "",
    CMName04: "",
    CMPosition04: "",
    CMName05: "",
    CMPosition05: "",
    CMName06: "",
    CMPosition06: "",
    CMName07: "",
    CMPosition07: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  const hasEditPermission = profile?.OfficeID === formData.OfficeID || profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสสำนักงาน",
      selector: (row) => row.OfficeID,
      sortable: true,
    },
    {
      name: "ชื่อสำนักงาน",
      selector: (row) => row.OfficeName,
      sortable: true,
    },
    {
      name: "ประเภทสำนักงาน",
      selector: (row) => {
        const type = officeTypes.find((t) => t.OfficeTypeID === row.OfficeType);
        return type ? type.OfficeTypeName : row.OfficeType || "-";
      },
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) =>

          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="รายละเอียด"
          />,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchOfficeTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOfficeTypes(response.OfficeTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching office types:", error);
    }
  };

  const fetchOffices = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeWithCommitteeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.OfficeList || [];
        setOffices(data);
        setFilteredOffices(data);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchOffices();
      fetchOfficeTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterType, filterStatus);
  };

  const handleTypeFilter = (e) => {
    const value = e.target.value;
    setFilterType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterType, value);
  };

  const applyFilters = (search, type, status) => {
    let filtered = offices;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const typeName =
          officeTypes.find((t) => t.OfficeTypeID === row.OfficeType)?.OfficeTypeName ||
          row.OfficeType ||
          "";
        return searchTerms.every((term) =>
          [
            row.OfficeID.toLowerCase(),
            row.OfficeName.toLowerCase(),
            typeName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (type) {
      filtered = filtered.filter((row) => row.OfficeType === type);
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredOffices(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterStatus("");
    setFilteredOffices(offices);
  };

  const handleAddNew = () => {
    setFormData({
      OfficeID: "",
      OfficeType: "",
      OfficeName: "",
      RegionalOffice: "",
      OfficeName2: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Email: "",
      Phone: "",
      Website: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
      CMOrderNo: "",
      CMName01: "",
      CMPosition01: "",
      CMName02: "",
      CMPosition02: "",
      CMName03: "",
      CMPosition03: "",
      CMName04: "",
      CMPosition04: "",
      CMName05: "",
      CMPosition05: "",
      CMName06: "",
      CMPosition06: "",
      CMName07: "",
      CMPosition07: "",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Status: row.Status ? "1" : "0",
      Latitude: row.Latitude || "",
      Longitude: row.Longitude || "",
      CMOrderNo: row.CMOrderNo || "",
      CMName01: row.CMName01 || "",
      CMPosition01: row.CMPosition01 || "",
      CMName02: row.CMName02 || "",
      CMPosition02: row.CMPosition02 || "",
      CMName03: row.CMName03 || "",
      CMPosition03: row.CMPosition03 || "",
      CMName04: row.CMName04 || "",
      CMPosition04: row.CMPosition04 || "",
      CMName05: row.CMName05 || "",
      CMPosition05: row.CMPosition05 || "",
      CMName06: row.CMName06 || "",
      CMPosition06: row.CMPosition06 || "",
      CMName07: row.CMName07 || "",
      CMPosition07: row.CMPosition07 || "",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "1" : "0" });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "OfficeID":
      case "OfficeName":
      case "OfficeType":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "กรุณากรอกอีเมลที่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "PostalCode":
        if (value && !/^\d{5}$/.test(value)) {
          newErrors[name] = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก";
        } else {
          delete newErrors[name];
        }
        break;
      case "Latitude":
      case "Longitude":
        if (value && isNaN(value)) {
          newErrors[name] = "ต้องเป็นตัวเลข";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.OfficeID &&
      formData.OfficeName &&
      formData.OfficeType &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateDLDOfficeByID"
        : "/createDLDOffice";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Status: formData.Status === "1",
          Latitude: formData.Latitude ? parseFloat(formData.Latitude) : null,
          Longitude: formData.Longitude ? parseFloat(formData.Longitude) : null,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตสำนักงานสำเร็จ!" : "สร้างสำนักงานสำเร็จ!");
        setShowForm(false);
        fetchOffices();
      }
    } catch (error) {
      console.error("Error saving office:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      OfficeID: "",
      OfficeType: "",
      OfficeName: "",
      RegionalOffice: "",
      OfficeName2: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Email: "",
      Phone: "",
      Website: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
      CMOrderNo: "",
      CMName01: "",
      CMPosition01: "",
      CMName02: "",
      CMPosition02: "",
      CMName03: "",
      CMPosition03: "",
      CMName04: "",
      CMPosition04: "",
      CMName05: "",
      CMPosition05: "",
      CMName06: "",
      CMPosition06: "",
      CMName07: "",
      CMPosition07: "",
    });
    setErrors({});
  };

  const handleDelete = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการลบข้อมูล");
      return;
    }
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบสำนักงานนี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateDLDOfficeByID",
        data: {
          ...formData,
          Status: false,
          UpdatedBy: sessionId,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("ลบสำนักงานสำเร็จ!");
        setShowForm(false);
        fetchOffices();
      }
    } catch (error) {
      console.error("Error deleting office:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลสำนักงาน
            </Heading>
          </HStack>
        </Box>

        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลสำนักงาน" : "เพิ่มข้อมูลสำนักงาน"}
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.OfficeID}
                  isReadOnly={!hasEditPermission}
                  isDisabled={isEditing && hasEditPermission}
                >
                  <FormLabel>รหัสสำนักงาน</FormLabel>
                  <Input
                    name="OfficeID"
                    value={formData.OfficeID}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.OfficeID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.OfficeName}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>ชื่อสำนักงาน</FormLabel>
                  <Input
                    name="OfficeName"
                    value={formData.OfficeName}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.OfficeName}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.OfficeType}
                  isDisabled={!hasEditPermission}
                >
                  <FormLabel>ประเภทสำนักงาน</FormLabel>
                  <Select
                    name="OfficeType"
                    value={formData.OfficeType}
                    onChange={handleInputChange}
                    placeholder="เลือกประเภท"
                    colorScheme="iBlue"
                  >
                    {officeTypes.map((type) => (
                      <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                        {type.OfficeTypeName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.OfficeType}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ชื่อสำนักงาน (ภาษาอังกฤษ)</FormLabel>
                  <Input
                    name="OfficeName2"
                    value={formData.OfficeName2}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>สังกัดสำนักงานปศุสัตว์เขต</FormLabel>
                  <Input
                    name="RegionalOffice"
                    value={formData.RegionalOffice}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>เลขที่อยู่ / อาคาร / ชั้น</FormLabel>
                  <Input
                    name="AddressNo"
                    value={formData.AddressNo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หมู่ที่</FormLabel>
                  <Input
                    name="AddressMoo"
                    value={formData.AddressMoo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ถนน</FormLabel>
                  <Input
                    name="AddressRoad"
                    value={formData.AddressRoad}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำบล</FormLabel>
                  <Input
                    name="AddressSubdistrict"
                    value={formData.AddressSubdistrict}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>อำเภอ</FormLabel>
                  <Input
                    name="AddressDistrict"
                    value={formData.AddressDistrict}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>จังหวัด</FormLabel>
                  <Input
                    name="Province"
                    value={formData.Province}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isInvalid={!!errors.PostalCode} isReadOnly={!hasEditPermission}>
                  <FormLabel>รหัสไปรษณีย์</FormLabel>
                  <Input
                    name="PostalCode"
                    value={formData.PostalCode}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.PostalCode}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isInvalid={!!errors.Email} isReadOnly={!hasEditPermission}>
                  <FormLabel>อีเมล</FormLabel>
                  <Input
                    name="Email"
                    value={formData.Email}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Email}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>โทรศัพท์</FormLabel>
                  <Input
                    name="Phone"
                    value={formData.Phone}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>Website</FormLabel>
                  <Input
                    name="Website"
                    value={formData.Website}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isInvalid={!!errors.Latitude} isReadOnly={!hasEditPermission}>
                  <FormLabel>Latitude</FormLabel>
                  <Input
                    name="Latitude"
                    value={formData.Latitude}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Latitude}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isInvalid={!!errors.Longitude} isReadOnly={!hasEditPermission}>
                  <FormLabel>Longitude</FormLabel>
                  <Input
                    name="Longitude"
                    value={formData.Longitude}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Longitude}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={4}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isDisabled={!hasEditPermission}>
                  <FormLabel>สถานะ</FormLabel>
                  <Switch
                    name="Status"
                    isChecked={formData.Status === "1"}
                    onChange={handleStatusChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={4}>
                  <StandardHeader
                        title="ข้อมูลการแต่งตั้งคณะกรรมการ ตรวจนับวัคซีน (ตามผช. 37)"
                        bgColor="iBlue.500"
                        icon={faSearch}
                  />
                <Heading size="sm" mt={6} mb={4}>ข้อมูลคณะกรรมการ</Heading>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>เลขที่คำสั่งแต่งตั้ง</FormLabel>
                  <Input
                    name="CMOrderNo"
                    value={formData.CMOrderNo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2} />
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ประธานกรรมการ</FormLabel>
                  <Input
                    name="CMName01"
                    value={formData.CMName01}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งประธานกรรมการ</FormLabel>
                  <Input
                    name="CMPosition01"
                    value={formData.CMPosition01}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>กรรมการ #1</FormLabel>
                  <Input
                    name="CMName02"
                    value={formData.CMName02}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งกรรมการ #1</FormLabel>
                  <Input
                    name="CMPosition02"
                    value={formData.CMPosition02}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>กรรมการ #2</FormLabel>
                  <Input
                    name="CMName03"
                    value={formData.CMName03}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งกรรมการ #2</FormLabel>
                  <Input
                    name="CMPosition03"
                    value={formData.CMPosition03}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>กรรมการ #3</FormLabel>
                  <Input
                    name="CMName04"
                    value={formData.CMName04}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งกรรมการ #3</FormLabel>
                  <Input
                    name="CMPosition04"
                    value={formData.CMPosition04}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ผู้อนุมัติ</FormLabel>
                  <Input
                    name="CMName05"
                    value={formData.CMName05}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งผู้อนุมัติ</FormLabel>
                  <Input
                    name="CMPosition05"
                    value={formData.CMPosition05}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ผู้จัดทำรายงาน</FormLabel>
                  <Input
                    name="CMName06"
                    value={formData.CMName06}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งผู้จัดทำรายงาน</FormLabel>
                  <Input
                    name="CMPosition06"
                    value={formData.CMPosition06}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ผู้ตรวจสอบรายงาน</FormLabel>
                  <Input
                    name="CMName07"
                    value={formData.CMName07}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ตำแหน่งผู้ตรวจสอบรายงาน</FormLabel>
                  <Input
                    name="CMPosition07"
                    value={formData.CMPosition07}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {hasEditPermission && isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              {hasEditPermission && (
                <IconButton.Save onClick={handleSave} isLoading={isLoading} />
              )}
            </HStack>
          </Box>
        )}

        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาสำนักงาน..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterType}
              onChange={handleTypeFilter}
              placeholder="เลือกประเภทสำนักงาน"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {officeTypes.map((type) => (
                <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                  {type.OfficeTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          {hasEditPermission && (
            <IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" />
          )}
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : offices.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลสำนักงาน
          </Text>
        ) : filteredOffices.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredOffices}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default DLDOfficeManagement;